import { DocumentNode } from '@apollo/client';
import clsx from 'clsx';
import { differenceInMonths } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { JobPosition } from '@pflegia/data-access';
import { CareType } from '@pflegia/types/api/employer/care-type';
import { EmployerType } from '@pflegia/types/api/employer/employer-type';
import { GeoLocation } from '@pflegia/types/api/location/geolocation';
import { BasePositionCardProps } from '@pflegia/types/common/interfaces/position-card';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { ReactComponent as Briefcase } from '@pflegia/ui/icons/briefcase.svg';
import { ReactComponent as MapPin } from '@pflegia/ui/icons/map-pin.svg';
import { CardStatus } from '@pflegia/ui/pflegia/types/card';
import { useParseSalary } from '@pflegia/ui/shared/hooks/use-parse-salary';
import { InitiativeBadge } from '@pflegia/ui/shared/initiative-badge/InitiativeBadge';
import { FeaturesBadges } from '@pflegia/ui/shared/job-position/FeaturesBadges';
import { shouldImageBeUnoptimized } from '@pflegia/ui/shared/utils/image-unoptimized';
import { formatLocationDisplay, getJobPositionPathname } from '@pflegia/util/functions/job-position/helpers';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import { careTypeToTypeOfEmployer } from '@shared/application/care-type-to-type-of-employer';
import { CHAT_BY_JOB_POSITION_ID, MONTHS_BEFORE_SALARY_EXPIRY } from '@shared/application/constants';
import { PositionCardButtons } from '@shared/components/position-card/PositionCardButtons.component';
import { useNavigationHelper } from '@shared/hooks/use-navigation-helper';
import { MAGAZIN_UTM_CONTENT, MagazinAttribution, getMagazinUtmRecord } from '@shared/utils/magazin';

export interface SlimPositionCardProps extends Pick<BasePositionCardProps, 'featuresBadges' | 'type' | 'status' | 'positionLinkTarget'> {
  basePathOverride?: string;
  cardClassName?: string;
  isLinkLoadingDisabled?: boolean;
  headingsClassName?: string;
  employerClassName?: string;
  magazinAttribution?: MagazinAttribution;
  jobPosition: Pick<
    JobPosition,
    | 'employer'
    | 'fullTimeHours'
    | 'geoLocation'
    | 'headline'
    | 'id'
    | 'salaryHigh'
    | 'salaryLow'
    | 'showSalary'
    | 'isInitiative'
    | 'salaryLastChangedAt'
    | 'title'
    | 'status'
    | 'slug'
    | 'createdAt'
  >;
  openDialog?: (connectionInfo: ConnectionInfo) => void;
  compact?: boolean;
  refetchQueries?: DocumentNode[];
  onChatClick?: () => void;
  onPositionLinkClick?: () => void;
}

export const SlimPositionCard: React.FC<SlimPositionCardProps> = ({
  basePathOverride,
  cardClassName,
  isLinkLoadingDisabled = false,
  headingsClassName,
  employerClassName,
  jobPosition,
  openDialog,
  status,
  type = 'default',
  compact,
  refetchQueries,
  featuresBadges,
  positionLinkTarget,
  onChatClick,
  onPositionLinkClick,
  magazinAttribution,
}) => {
  const { employer, geoLocation, isInitiative } = jobPosition;
  const router = useRouter();
  const { source, applicationSource, registerOrigin, utmParams, basePath } = useNavigationHelper();

  const employerLogo = getPublicImageUrl(employer.logoKey) || '';
  const typeOfEmployer = careTypeToTypeOfEmployer(employer.careType as CareType);
  const salary = useParseSalary(
    { low: jobPosition.salaryLow ?? undefined, high: jobPosition.salaryHigh ?? undefined },
    typeOfEmployer === EmployerType.Leasing,
    jobPosition.fullTimeHours ?? 0,
  );
  const jobId = jobPosition.id;
  const jobTitle = jobPosition.title;
  const { slug } = jobPosition;

  // On magazine article pages each CTA carries its own utm_content: the job title link
  // uses `current-jobs-job-title`, the "Mehr erfahren" link uses `current-jobs-more-info`.
  // Off magazine pages `magazinAttribution` is undefined and behaviour is unchanged.
  const titleUtmParams = magazinAttribution ? getMagazinUtmRecord(magazinAttribution, MAGAZIN_UTM_CONTENT.currentJobsJobTitle) : utmParams;

  const positionLink = getJobPositionPathname(
    { geoLocation, id: jobId, title: jobTitle, slug },
    { basePath: basePathOverride ?? basePath, source, applicationSource, registerOrigin, slimmed: true, utmParams: titleUtmParams },
  );

  const learnMoreLink = magazinAttribution
    ? getJobPositionPathname(
        { geoLocation, id: jobId, title: jobTitle, slug },
        {
          basePath: basePathOverride ?? basePath,
          source,
          applicationSource,
          registerOrigin,
          slimmed: true,
          utmParams: getMagazinUtmRecord(magazinAttribution, MAGAZIN_UTM_CONTENT.currentJobsMoreInfo),
        },
      )
    : positionLink;

  const handleChatClick = useCallback(() => {
    if (onChatClick) {
      onChatClick();
    } else {
      router.push(CHAT_BY_JOB_POSITION_ID(jobId));
    }
  }, [onChatClick, router, jobId]);

  const showSalary = useMemo(() => {
    if (!employer.showSalary || !jobPosition.showSalary) return false;
    if (!isInitiative) return true;

    const salaryUpdatedAt = jobPosition.salaryLastChangedAt ?? jobPosition.createdAt;
    const diffInMonths = differenceInMonths(new Date(), salaryUpdatedAt);
    return diffInMonths <= MONTHS_BEFORE_SALARY_EXPIRY;
  }, [employer.showSalary, jobPosition.showSalary, isInitiative, jobPosition.createdAt, jobPosition.salaryLastChangedAt]);

  return (
    <div className={twMerge(clsx('flex h-full w-full flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-xl', cardClassName))}>
      <div className={clsx('flex justify-between gap-x-2', !compact && 'flex-grow')}>
        <div className={clsx('flex flex-col', compact ? 'gap-y-0.5' : 'gap-y-1')}>
          {isInitiative && <InitiativeBadge className='mb-4' />}
          <FeaturesBadges featuresBadges={featuresBadges} />
          <Link href={positionLink} target={positionLinkTarget} onClick={onPositionLinkClick} className='no-underline! hover:underline'>
            <h2
              className={twMerge(
                clsx('text-default text-base font-semibold break-words hyphens-auto hover:underline md:text-2xl', headingsClassName),
              )}>
              {jobPosition.headline ?? jobPosition.title}
            </h2>
          </Link>
          <h3 className={twMerge(clsx('text-secondary text-sm md:text-2xl', employerClassName))}>{jobPosition.employer?.name}</h3>
        </div>
        {employerLogo && (
          <div className='bg-img relative h-12 w-12 shrink-0 rounded-lg drop-shadow-md md:h-20 md:w-20'>
            <Image
              alt=''
              className='rounded-lg object-contain'
              fill
              quality={75}
              sizes='80px'
              src={employerLogo}
              unoptimized={shouldImageBeUnoptimized(employerLogo)}
            />
          </div>
        )}
      </div>
      <div className={clsx(compact ? 'mt-auto gap-y-3 pt-3' : 'mt-6 gap-y-6', 'flex flex-col justify-between', !compact && 'md:flex-row')}>
        <div className={clsx('flex flex-col gap-y-2 md:gap-x-6', !compact && 'lg:flex-row')}>
          {jobPosition?.geoLocation && (
            <div className='flex items-center gap-x-2 text-sm md:text-lg'>
              <MapPin data-testid='map-pin-icon' className='text-funnel-blue shrink-0' width={24} height={24} />
              <span>
                {formatLocationDisplay(
                  jobPosition.geoLocation.formattedAddress,
                  jobPosition.geoLocation.postalCode || undefined,
                  jobPosition.geoLocation.city || '',
                )}
              </span>
            </div>
          )}
          {showSalary && salary && (
            <div className='flex items-center gap-x-2 text-sm md:text-lg'>
              <Briefcase data-testid='briefcase-icon' className='text-funnel-blue shrink-0' width={24} height={24} />
              <span>{salary}</span>
            </div>
          )}
        </div>
        <div className={clsx(compact && 'mt-auto w-full pt-3 whitespace-nowrap')}>
          <PositionCardButtons
            openDialog={openDialog as (connectionInfo: ConnectionInfo) => void}
            connectionStatus={status as CardStatus}
            connectionType={type}
            isLinkLoadingDisabled={isLinkLoadingDisabled}
            jobId={jobPosition.id}
            slug={jobPosition.slug}
            geoLocation={jobPosition.geoLocation as GeoLocation}
            jobTitle={jobPosition.title}
            shouldShowDetails
            employerName={employer.name}
            slimmed
            positionLink={learnMoreLink}
            refetchQueries={refetchQueries}
            target={positionLinkTarget}
            onChatClick={handleChatClick}
            magazinAttribution={magazinAttribution}
          />
        </div>
      </div>
    </div>
  );
};
export default SlimPositionCard;
